// ExploreDataDashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Download,
  ChevronDown,
  ChevronUp,
  FileText,
  Book,
  BookOpen,
  ListOrdered,
  Building,
  HelpCircle,
  PieChart,
  BarChart,
  FileQuestion,
  Clock,
  Calendar,
  Layers,
  File,
  Eye
} from 'lucide-react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit
} from 'firebase/firestore';

const ExploreDataDashboard = () => {
  // Hierarchy selection states
  const [boards, setBoards] = useState([]);
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [selectedStd, setSelectedStd] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  
  // Data states
  const [questions, setQuestions] = useState([]);
  const [theories, setTheories] = useState([]);
  const [samplePapers, setSamplePapers] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [questionTypeFilter, setQuestionTypeFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  
  // Analytics states
  const [contentStats, setContentStats] = useState({
    totalQuestions: 0,
    totalTheories: 0,
    totalSamplePapers: 0,
    mcqQuestions: 0,
    descriptiveQuestions: 0,
    questionsByDifficulty: { Easy: 0, Medium: 0, Hard: 0 },
    contentByYear: {}
  });
  
  // Collapsible sections
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [statsExpanded, setStatsExpanded] = useState(true);
  
  // Fetch boards on component mount
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const boardsRef = collection(db, "boards");
        const snapshot = await getDocs(boardsRef);
        const boardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBoards(boardsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching boards:", error);
        setLoading(false);
      }
    };
    fetchBoards();
  }, []);

  // Fetch standards when board is selected
  useEffect(() => {
    const fetchStandards = async () => {
      if (!selectedBoard) {
        setStandards([]);
        return;
      }
      
      try {
        setLoading(true);
        const standardsRef = collection(db, "standards");
        const q = query(standardsRef, where("boardId", "==", selectedBoard));
        const snapshot = await getDocs(q);
        const standardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setStandards(standardsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching standards:", error);
        setLoading(false);
      }
    };

    fetchStandards();
    setSelectedStd(null);
    setSelectedSubject(null);
    setSelectedChapter(null);
    setSelectedTopic(null);
  }, [selectedBoard]);

  // Fetch subjects when standard is selected
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!selectedStd) {
        setSubjects([]);
        return;
      }
    
      try {
        setLoading(true);
        const subjectsRef = collection(db, "subjects");
        const q = query(subjectsRef, where("standardId", "==", selectedStd));
        const snapshot = await getDocs(q);
        const subjectsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubjects(subjectsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching subjects:", error);
        setLoading(false);
      }
    };    

    fetchSubjects();
    setSelectedSubject(null);
    setSelectedChapter(null);
    setSelectedTopic(null);
  }, [selectedStd]);

  // Fetch chapters when subject is selected
  useEffect(() => {
    const fetchChapters = async () => {
      if (!selectedSubject) {
        setChapters([]);
        return;
      }
    
      try {
        setLoading(true);
        const chaptersRef = collection(db, "chapters");
        const q = query(chaptersRef, where("subjectId", "==", selectedSubject));
        const snapshot = await getDocs(q);
        const chaptersList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setChapters(chaptersList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching chapters:", error);
        setLoading(false);
      }
    };    

    fetchChapters();
    setSelectedChapter(null);
    setSelectedTopic(null);
  }, [selectedSubject]);

  // Fetch topics when chapter is selected
  useEffect(() => {
    const fetchTopics = async () => {
      if (!selectedChapter) {
        setTopics([]);
        return;
      }
  
      try {
        setLoading(true);
        const topicsRef = collection(db, "topics");
        const q = query(topicsRef, where("chapterId", "==", selectedChapter));
        const snapshot = await getDocs(q);
        const topicsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTopics(topicsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching topics:", error);
        setLoading(false);
      }
    };
  
    fetchTopics();
    setSelectedTopic(null);
  }, [selectedChapter]);

  // Fetch all content when filters change
  useEffect(() => {
    const fetchAllContent = async () => {
      // Define our target scope for content fetch
      let targetScope = { type: "none", id: null };
      
      if (selectedTopic) {
        targetScope = { type: "topic", id: selectedTopic };
      } else if (selectedChapter) {
        targetScope = { type: "chapter", id: selectedChapter };
      } else if (selectedSubject) {
        targetScope = { type: "subject", id: selectedSubject };
      } else if (selectedStd) {
        targetScope = { type: "standard", id: selectedStd };
      } else if (selectedBoard) {
        targetScope = { type: "board", id: selectedBoard };
      } else {
        // If nothing is selected, don't fetch anything yet
        return;
      }
      
      setLoading(true);
      
      try {
        // Fetch Questions
        let questionsRef = collection(db, "questions");
        let questionsQuery;
        
        if (targetScope.type === "topic") {
          questionsQuery = query(questionsRef, where("topicId", "==", targetScope.id));
        } else if (targetScope.type === "chapter") {
          questionsQuery = query(questionsRef, where("chapterId", "==", targetScope.id));
        } else if (targetScope.type === "subject") {
          questionsQuery = query(questionsRef, where("subjectId", "==", targetScope.id));
        } else if (targetScope.type === "standard") {
          questionsQuery = query(questionsRef, where("standardId", "==", targetScope.id));
        } else if (targetScope.type === "board") {
          questionsQuery = query(questionsRef, where("boardId", "==", targetScope.id));
        }
        
        const questionsSnapshot = await getDocs(questionsQuery);
        const questionsList = questionsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setQuestions(questionsList);
        
        // Fetch Theories
        let theoriesRef = collection(db, "theories");
        let theoriesQuery;
        
        if (targetScope.type === "topic") {
          theoriesQuery = query(theoriesRef, where("topicId", "==", targetScope.id));
        } else if (targetScope.type === "chapter") {
          theoriesQuery = query(theoriesRef, where("chapterId", "==", targetScope.id));
        } else if (targetScope.type === "subject") {
          theoriesQuery = query(theoriesRef, where("subjectId", "==", targetScope.id));
        } else if (targetScope.type === "standard") {
          theoriesQuery = query(theoriesRef, where("standardId", "==", targetScope.id));
        } else if (targetScope.type === "board") {
          theoriesQuery = query(theoriesRef, where("boardId", "==", targetScope.id));
        }
        
        const theoriesSnapshot = await getDocs(theoriesQuery);
        const theoriesList = theoriesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTheories(theoriesList);
        
        // Fetch Sample Papers
        let papersRef = collection(db, "samplePapers");
        let papersQuery;
        
        if (targetScope.type === "topic") {
          // Sample papers might not have topic IDs
          papersQuery = query(papersRef, where("chapterId", "==", selectedChapter));
        } else if (targetScope.type === "chapter") {
          papersQuery = query(papersRef, where("chapterId", "==", targetScope.id));
        } else if (targetScope.type === "subject") {
          papersQuery = query(papersRef, where("subjectId", "==", targetScope.id));
        } else if (targetScope.type === "standard") {
          papersQuery = query(papersRef, where("standardId", "==", targetScope.id));
        } else if (targetScope.type === "board") {
          papersQuery = query(papersRef, where("boardId", "==", targetScope.id));
        }
        
        const papersSnapshot = await getDocs(papersQuery);
        const papersList = papersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSamplePapers(papersList);
        
        // Calculate statistics
        calculateContentStats(questionsList, theoriesList, papersList);
        
      } catch (error) {
        console.error("Error fetching content:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllContent();
  }, [selectedBoard, selectedStd, selectedSubject, selectedChapter, selectedTopic]);

  // Calculate statistics from the fetched data
  const calculateContentStats = (questions, theories, papers) => {
    const mcqCount = questions.filter(q => q.type === 'mcq').length;
    const descriptiveCount = questions.filter(q => q.type === 'descriptive').length;
    
    // Count questions by difficulty
    const difficultyMap = { Easy: 0, Medium: 0, Hard: 0 };
    questions.forEach(q => {
      if (q.difficultyLevel && difficultyMap[q.difficultyLevel] !== undefined) {
        difficultyMap[q.difficultyLevel]++;
      }
    });
    
    // Group content by year
    const yearsMap = {};
    
    // Questions by year (assuming createdAt field)
    questions.forEach(q => {
      let year;
      if (q.createdAt) {
        year = q.createdAt.toDate ? q.createdAt.toDate().getFullYear() : new Date(q.createdAt).getFullYear();
      } else if (q.questionYear) {
        year = q.questionYear;
      } else {
        return; // Skip if no year info
      }
      
      if (!yearsMap[year]) {
        yearsMap[year] = { questions: 0, theories: 0, papers: 0 };
      }
      yearsMap[year].questions++;
    });
    
    // Theories by year
    theories.forEach(t => {
      if (!t.createdAt) return;
      const year = t.createdAt.toDate ? t.createdAt.toDate().getFullYear() : new Date(t.createdAt).getFullYear();
      if (!yearsMap[year]) {
        yearsMap[year] = { questions: 0, theories: 0, papers: 0 };
      }
      yearsMap[year].theories++;
    });
    
    // Papers by year
    papers.forEach(p => {
      const year = p.year || (p.createdAt ? (p.createdAt.toDate ? p.createdAt.toDate().getFullYear() : new Date(p.createdAt).getFullYear()) : null);
      if (!year) return;
      
      if (!yearsMap[year]) {
        yearsMap[year] = { questions: 0, theories: 0, papers: 0 };
      }
      yearsMap[year].papers++;
    });
    
    setContentStats({
      totalQuestions: questions.length,
      totalTheories: theories.length,
      totalSamplePapers: papers.length,
      mcqQuestions: mcqCount,
      descriptiveQuestions: descriptiveCount,
      questionsByDifficulty: difficultyMap,
      contentByYear: yearsMap
    });
  };

  // Filter data based on search and filters
  const getFilteredContent = () => {
    let filteredQuestions = questions;
    let filteredTheories = theories;
    let filteredPapers = samplePapers;
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      
      filteredQuestions = filteredQuestions.filter(q => 
        (q.question?.text && q.question.text.toLowerCase().includes(query)) || 
        (q.solution && q.solution.toLowerCase().includes(query))
      );
      
      filteredTheories = filteredTheories.filter(t => 
        (t.title && t.title.toLowerCase().includes(query)) || 
        (t.description && t.description.toLowerCase().includes(query))
      );
      
      filteredPapers = filteredPapers.filter(p => 
        (p.title && p.title.toLowerCase().includes(query)) || 
        (p.description && p.description.toLowerCase().includes(query))
      );
    }
    
    // Apply difficulty filter to questions
    if (difficultyFilter !== 'all') {
      filteredQuestions = filteredQuestions.filter(q => 
        q.difficultyLevel === difficultyFilter
      );
    }
    
    // Apply question type filter
    if (questionTypeFilter !== 'all') {
      filteredQuestions = filteredQuestions.filter(q => 
        q.type === questionTypeFilter
      );
    }
    
    // Apply year filter to all content
    if (yearFilter !== 'all') {
      const targetYear = parseInt(yearFilter);
      
      filteredQuestions = filteredQuestions.filter(q => {
        if (q.questionYear) return parseInt(q.questionYear) === targetYear;
        if (q.createdAt) {
          const year = q.createdAt.toDate ? q.createdAt.toDate().getFullYear() : new Date(q.createdAt).getFullYear();
          return year === targetYear;
        }
        return false;
      });
      
      filteredTheories = filteredTheories.filter(t => {
        if (!t.createdAt) return false;
        const year = t.createdAt.toDate ? t.createdAt.toDate().getFullYear() : new Date(t.createdAt).getFullYear();
        return year === targetYear;
      });
      
      filteredPapers = filteredPapers.filter(p => {
        if (p.year) return parseInt(p.year) === targetYear;
        if (p.createdAt) {
          const year = p.createdAt.toDate ? p.createdAt.toDate().getFullYear() : new Date(p.createdAt).getFullYear();
          return year === targetYear;
        }
        return false;
      });
    }
    
    return { filteredQuestions, filteredTheories, filteredPapers };
  };
  
  const { filteredQuestions, filteredTheories, filteredPapers } = getFilteredContent();
  
  // Get available years for the filter dropdown
  const getAvailableYears = () => {
    return Object.keys(contentStats.contentByYear).sort((a, b) => b - a); // Sort in descending order
  };
  
  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  
  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Data Explorer</h1>
          <div className="text-sm text-gray-500">
            {selectedBoard && boards.find(b => b.id === selectedBoard)?.name}
            {selectedStd && ` › ${standards.find(s => s.id === selectedStd)?.name}`}
            {selectedSubject && ` › ${subjects.find(s => s.id === selectedSubject)?.name}`}
            {selectedChapter && ` › ${chapters.find(c => c.id === selectedChapter)?.name}`}
            {selectedTopic && ` › ${topics.find(t => t.id === selectedTopic)?.name}`}
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar - Navigation Hierarchy */}
        <div className="w-64 bg-white h-[calc(100vh-64px)] border-r border-gray-200 p-4 overflow-y-auto">
          <div className="mb-6">
            <h2 className="font-semibold text-gray-700 mb-2 flex items-center">
              <Building className="w-4 h-4 mr-2" />
              Boards
            </h2>
            <div className="space-y-1 ml-6">
              {boards.map(board => (
                <button
                  key={board.id}
                  onClick={() => setSelectedBoard(board.id)}
                  className={`w-full text-left px-2 py-1 rounded-md text-sm ${
                    selectedBoard === board.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50'
                  }`}
                >
                  {board.name}
                </button>
              ))}
            </div>
          </div>

          {selectedBoard && (
            <div className="mb-6">
              <h2 className="font-semibold text-gray-700 mb-2 flex items-center">
                <Book className="w-4 h-4 mr-2" />
                Standards
              </h2>
              <div className="space-y-1 ml-6">
                {standards.map(std => (
                  <button
                    key={std.id}
                    onClick={() => setSelectedStd(std.id)}
                    className={`w-full text-left px-2 py-1 rounded-md text-sm ${
                      selectedStd === std.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50'
                    }`}
                  >
                    {std.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedStd && (
            <div className="mb-6">
              <h2 className="font-semibold text-gray-700 mb-2 flex items-center">
                <BookOpen className="w-4 h-4 mr-2" />
                Subjects
              </h2>
              <div className="space-y-1 ml-6">
                {subjects.map(subject => (
                  <button
                    key={subject.id}
                    onClick={() => setSelectedSubject(subject.id)}
                    className={`w-full text-left px-2 py-1 rounded-md text-sm ${
                      selectedSubject === subject.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50'
                    }`}
                  >
                    {subject.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedSubject && (
            <div className="mb-6">
              <h2 className="font-semibold text-gray-700 mb-2 flex items-center">
                <ListOrdered className="w-4 h-4 mr-2" />
                Chapters
              </h2>
              <div className="space-y-1 ml-6">
                {chapters.map(chapter => (
                  <button
                    key={chapter.id}
                    onClick={() => setSelectedChapter(chapter.id)}
                    className={`w-full text-left px-2 py-1 rounded-md text-sm ${
                      selectedChapter === chapter.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50'
                    }`}
                  >
                    {chapter.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedChapter && (
            <div className="mb-6">
              <h2 className="font-semibold text-gray-700 mb-2 flex items-center">
                <Layers className="w-4 h-4 mr-2" />
                Topics
              </h2>
              <div className="space-y-1 ml-6">
                {topics.length > 0 ? (
                  topics.map(topic => (
                    <button
                      key={topic.id}
                      onClick={() => setSelectedTopic(topic.id)}
                      className={`w-full text-left px-2 py-1 rounded-md text-sm ${
                        selectedTopic === topic.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50'
                      }`}
                    >
                      {topic.name}
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">No topics found</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              {/* Content Search and Filters */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-semibold">Filters</h2>
                  <button 
                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {filtersExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>
                
                {filtersExpanded && (
                  <div className="bg-white rounded-lg shadow p-4 transition-all duration-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Search box */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search content..."
                          className="w-full p-2 pl-8 border border-gray-300 rounded-lg"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Search className="absolute left-2 top-2 w-4 h-4 text-gray-400" />
                      </div>
                      
                      {/* Question difficulty filter (only if questions exist) */}
                      {contentStats.totalQuestions > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                          <select
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            value={difficultyFilter}
                            onChange={(e) => setDifficultyFilter(e.target.value)}
                          >
                            <option value="all">All Difficulties</option>
                            <option value="Easy">Easy</option>
                            <option value="Medium">Medium</option>
                            <option value="Hard">Hard</option>
                          </select>
                        </div>
                      )}
                      
                      {/* Question type filter (only if questions exist) */}
                      {contentStats.totalQuestions > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
                          <select
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            value={questionTypeFilter}
                            onChange={(e) => setQuestionTypeFilter(e.target.value)}
                          >
                            <option value="all">All Types</option>
                            <option value="mcq">MCQ</option>
                            <option value="descriptive">Descriptive</option>
                          </select>
                        </div>
                      )}
                      
                      {/* Year filter (only if years are available) */}
                      {getAvailableYears().length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                          <select
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            value={yearFilter}
                            onChange={(e) => setYearFilter(e.target.value)}
                          >
                            <option value="all">All Years</option>
                            {getAvailableYears().map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Content Statistics */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-semibold">Content Statistics</h2>
                  <button 
                    onClick={() => setStatsExpanded(!statsExpanded)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {statsExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>
                
                {statsExpanded && (
                  <div className="bg-white rounded-lg shadow p-4 transition-all duration-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      {/* Questions Stats */}
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-blue-800">Questions</h3>
                          <FileQuestion className="w-5 h-5 text-blue-500" />
                        </div>
                        <p className="text-2xl font-bold mt-2">{contentStats.totalQuestions}</p>
                        <div className="mt-2 text-sm text-blue-700">
                          <div>MCQ: {contentStats.mcqQuestions}</div>
                          <div>Descriptive: {contentStats.descriptiveQuestions}</div>
                        </div>
                      </div>
                      
                      {/* Theory Stats */}
                      <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-green-800">Theory Content</h3>
                          <FileText className="w-5 h-5 text-green-500" />
                        </div>
                        <p className="text-2xl font-bold mt-2">{contentStats.totalTheories}</p>
                      </div>
                      
                      {/* Sample Papers Stats */}
                      <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-purple-800">Sample Papers</h3>
                          <File className="w-5 h-5 text-purple-500" />
                        </div>
                        <p className="text-2xl font-bold mt-2">{contentStats.totalSamplePapers}</p>
                      </div>
                    </div>
                    
                    {/* Difficulty breakdown (if questions exist) */}
                    {contentStats.totalQuestions > 0 && (
                      <div className="mt-4">
                        <h3 className="font-medium text-gray-700 mb-2">Questions by Difficulty</h3>
                        <div className="flex items-center space-x-2">
                          <div className="bg-green-100 h-6 rounded-l-md" style={{ width: `${(contentStats.questionsByDifficulty.Easy / contentStats.totalQuestions) * 100}%` }}>
                            <div className="text-xs font-medium text-center py-1">Easy ({contentStats.questionsByDifficulty.Easy})</div>
                          </div>
                          <div className="bg-yellow-100 h-6" style={{ width: `${(contentStats.questionsByDifficulty.Medium / contentStats.totalQuestions) * 100}%` }}>
                            <div className="text-xs font-medium text-center py-1">Medium ({contentStats.questionsByDifficulty.Medium})</div>
                          </div>
                          <div className="bg-red-100 h-6 rounded-r-md" style={{ width: `${(contentStats.questionsByDifficulty.Hard / contentStats.totalQuestions) * 100}%` }}>
                            <div className="text-xs font-medium text-center py-1">Hard ({contentStats.questionsByDifficulty.Hard})</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Yearly breakdown (if years data exists) */}
                    {Object.keys(contentStats.contentByYear).length > 0 && (
                      <div className="mt-4">
                        <h3 className="font-medium text-gray-700 mb-2">Content by Year</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {Object.entries(contentStats.contentByYear)
                            .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
                            .map(([year, counts]) => (
                              <div key={year} className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                                <div className="font-medium">{year}</div>
                                <div className="text-sm mt-1">
                                  <div className="flex justify-between">
                                    <span>Questions:</span>
                                    <span>{counts.questions}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Theory Content:</span>
                                    <span>{counts.theories}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Sample Papers:</span>
                                    <span>{counts.papers}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Content Tabs */}
              <div className="mb-4">
                <div className="border-b border-gray-200">
                  <nav className="flex space-x-8">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'overview'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setActiveTab('questions')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'questions'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Questions ({filteredQuestions.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('theories')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'theories'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Theory Content ({filteredTheories.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('papers')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'papers'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Sample Papers ({filteredPapers.length})
                    </button>
                  </nav>
                </div>
              </div>
              
              {/* Tab Content */}
              <div className="bg-white shadow rounded-lg">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="p-6">
                    {filteredQuestions.length === 0 && filteredTheories.length === 0 && filteredPapers.length === 0 ? (
                      <div className="text-center py-8">
                        <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Content Found</h3>
                        <p className="text-gray-500 mb-6">Try selecting a different category or updating your filters.</p>
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-lg font-medium mb-4">Content Summary</h3>
                        
                        {/* Questions Summary (if any) */}
                        {filteredQuestions.length > 0 && (
                          <div className="mb-6">
                            <h4 className="font-medium text-gray-700 flex items-center">
                              <FileQuestion className="w-4 h-4 mr-2 text-blue-500" />
                              Questions ({filteredQuestions.length})
                            </h4>
                            <div className="ml-6 mt-2 text-sm">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-500">MCQ:</span>
                                  <span className="font-medium">{filteredQuestions.filter(q => q.type === 'mcq').length}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-500">Descriptive:</span>
                                  <span className="font-medium">{filteredQuestions.filter(q => q.type === 'descriptive').length}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-500">Easy:</span>
                                  <span className="font-medium">{filteredQuestions.filter(q => q.difficultyLevel === 'Easy').length}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-500">Medium:</span>
                                  <span className="font-medium">{filteredQuestions.filter(q => q.difficultyLevel === 'Medium').length}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-500">Hard:</span>
                                  <span className="font-medium">{filteredQuestions.filter(q => q.difficultyLevel === 'Hard').length}</span>
                                </div>
                              </div>
                              
                              <div className="mt-2">
                                <span className="text-gray-500">Recent Questions:</span>
                                <ul className="mt-1 list-disc list-inside ml-2">
                                  {filteredQuestions
                                    .sort((a, b) => {
                                      const dateA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : new Date(0);
                                      const dateB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)) : new Date(0);
                                      return dateB - dateA;
                                    })
                                    .slice(0, 3)
                                    .map(q => (
                                      <li key={q.id} className="text-gray-700 truncate">
                                        {q.question?.text || 'Untitled Question'}
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Theory Content Summary (if any) */}
                        {filteredTheories.length > 0 && (
                          <div className="mb-6">
                            <h4 className="font-medium text-gray-700 flex items-center">
                              <FileText className="w-4 h-4 mr-2 text-green-500" />
                              Theory Content ({filteredTheories.length})
                            </h4>
                            <div className="ml-6 mt-2 text-sm">
                              <div className="mt-2">
                                <span className="text-gray-500">Recent Uploads:</span>
                                <ul className="mt-1 list-disc list-inside ml-2">
                                  {filteredTheories
                                    .sort((a, b) => {
                                      const dateA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : new Date(0);
                                      const dateB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)) : new Date(0);
                                      return dateB - dateA;
                                    })
                                    .slice(0, 5)
                                    .map(t => (
                                      <li key={t.id} className="text-gray-700">
                                        <span className="font-medium">{t.title || 'Untitled'}</span>
                                        {t.description && <span className="text-gray-500 ml-2">- {t.description.substring(0, 50)}{t.description.length > 50 ? '...' : ''}</span>}
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Sample Papers Summary (if any) */}
                        {filteredPapers.length > 0 && (
                          <div className="mb-6">
                            <h4 className="font-medium text-gray-700 flex items-center">
                              <File className="w-4 h-4 mr-2 text-purple-500" />
                              Sample Papers ({filteredPapers.length})
                            </h4>
                            <div className="ml-6 mt-2 text-sm">
                              <div className="mt-2">
                                <span className="text-gray-500">Available Papers:</span>
                                <ul className="mt-1 list-disc list-inside ml-2">
                                  {filteredPapers
                                    .sort((a, b) => {
                                      // First try to sort by year
                                      const yearA = a.year || 0;
                                      const yearB = b.year || 0;
                                      if (yearA !== yearB) return yearB - yearA;
                                      
                                      // If years are the same, sort by created date
                                      const dateA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : new Date(0);
                                      const dateB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)) : new Date(0);
                                      return dateB - dateA;
                                    })
                                    .slice(0, 5)
                                    .map(p => (
                                      <li key={p.id} className="text-gray-700">
                                        <span className="font-medium">{p.title || 'Untitled'}</span>
                                        <span className="text-gray-500 ml-2">
                                          ({p.year || 'N/A'}{p.month ? `, ${p.month}` : ''})
                                        </span>
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Questions Tab */}
                {activeTab === 'questions' && (
                  <div className="overflow-x-auto">
                    {filteredQuestions.length === 0 ? (
                      <div className="text-center py-8">
                        <FileQuestion className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Questions Found</h3>
                        <p className="text-gray-500 mb-6">Try selecting a different category or updating your filters.</p>
                      </div>
                    ) : (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Question
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Difficulty
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Points
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Topic
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredQuestions.map((question) => (
                            <tr key={question.id}>
                              <td className="px-6 py-4 whitespace-normal">
                                <div className="text-sm font-medium text-gray-900">
                                  {question.question?.text || "No text available"}
                                </div>
                                {question.question?.images && question.question.images.length > 0 && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Has {question.question.images.length} image(s)
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  question.type === 'mcq' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {question.type === 'mcq' ? 'MCQ' : 'Descriptive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  question.difficultyLevel === 'Easy' ? 'bg-green-100 text-green-800' : 
                                  question.difficultyLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {question.difficultyLevel || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {question.points}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {topics.find(t => t.id === question.topicId)?.name || 'Unknown Topic'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
                
                {/* Theories Tab */}
                {activeTab === 'theories' && (
                  <div>
                    {filteredTheories.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Theory Content Found</h3>
                        <p className="text-gray-500 mb-6">Try selecting a different category or updating your filters.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                        {filteredTheories.map(theory => (
                          <div key={theory.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-4">
                              <h3 className="font-medium text-lg">{theory.title || "Untitled"}</h3>
                              {theory.description && (
                                <p className="text-gray-600 text-sm mt-1 line-clamp-2">{theory.description}</p>
                              )}
                              <div className="mt-4 flex justify-between text-xs text-gray-500">
                                <div className="flex items-center">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {formatDate(theory.createdAt)}
                                </div>
                                <div className="flex items-center">
                                  <FileText className="w-3 h-3 mr-1" />
                                  {formatFileSize(theory.fileSize)}
                                </div>
                              </div>
                              
                              <div className="mt-3 flex justify-end">
                                <a
                                  href={theory.pdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-blue-600 text-sm hover:text-blue-800"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View PDF
                                </a>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Sample Papers Tab */}
                {activeTab === 'papers' && (
                  <div>
                    {filteredPapers.length === 0 ? (
                      <div className="text-center py-8">
                        <File className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Sample Papers Found</h3>
                        <p className="text-gray-500 mb-6">Try selecting a different category or updating your filters.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                        {filteredPapers.map(paper => (
                          <div key={paper.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-4">
                              <div className="flex justify-between">
                                <h3 className="font-medium text-lg">{paper.title || "Untitled"}</h3>
                                <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded-full">
                                  {paper.year || 'N/A'}{paper.month ? ` ${paper.month}` : ''}
                                </span>
                              </div>
                              
                              {paper.description && (
                                <p className="text-gray-600 text-sm mt-1 line-clamp-2">{paper.description}</p>
                              )}
                              
                              <div className="mt-4 flex justify-between text-xs text-gray-500">
                                <div className="flex items-center">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {formatDate(paper.createdAt)}
                                </div>
                                <div className="flex items-center">
                                  <FileText className="w-3 h-3 mr-1" />
                                  {paper.fileName || 'Unknown file'}
                                </div>
                              </div>
                              
                              <div className="mt-3 flex justify-end">
                                <a
                                  href={paper.pdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-blue-600 text-sm hover:text-blue-800"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View Sample Paper
                                </a>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExploreDataDashboard;